/**
 * Onboarding Builder - Customizes Race Plans based on Onboarding Preferences
 *
 * This module extends the planbuilder to apply user preferences from onboarding:
 * - Places workouts only on selected training days
 * - Ensures long runs are on the user's preferred day
 * - Fills non-training days with rest workouts
 */
import { RacePlan } from "./dategrid";
import { dayOfWeek, DayDetails } from "types/app";
import { getDay } from "date-fns";

const DAY_MAP: { [key: number]: dayOfWeek } = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

/**
 * Checks if a workout is a rest day
 */
function isRestWorkout(workout: DayDetails | undefined): boolean {
  if (!workout) return true;
  const title = workout.title.toLowerCase();
  return title === "rest" || title.includes("rest");
}

/**
 * Checks if a workout is a long run based on type or title
 */
function isLongRun(workout: DayDetails | undefined): boolean {
  if (!workout) return false;
  if (workout.type === "Long") return true;
  const title = workout.title.toLowerCase();
  return title.includes("long") && !title.includes("medium");
}

/**
 * Checks if a workout is a race day.
 * Race days should never be moved or overwritten.
 */
function isRaceWorkout(workout: DayDetails | undefined): boolean {
  if (!workout) return false;
  if (workout.type === "Race") return true;
  const title = workout.title.toLowerCase();
  return title.includes("race");
}

/**
 * Creates a rest workout
 */
function createRestWorkout(): DayDetails {
  return {
    title: "Rest",
    desc: "",
    tags: ["Rest"],
    dist: [0],
    sourceUnits: "km",
  };
}

/**
 * Applies onboarding configuration to a race plan
 * Rearranges workouts to match user's training schedule and long run preferences
 */
export function applyOnboardingConfig(
  racePlan: RacePlan,
  trainingDays: string[],
  longRunDay: string,
): RacePlan {
  // Get the weeks from the date grid
  const weeks = racePlan.dateGrid.weeks;

  // DateGrid uses string keys internally, so we must use DateGrid#setEvent.
  const newDateGrid = racePlan.dateGrid.clone();

  // Process each week
  for (const week of weeks) {
    // Collect all non-rest workouts from the week
    const workouts: {
      workout: DayDetails;
      isLong: boolean;
      isRace: boolean;
      date: Date;
    }[] = [];
    const raceDates = new Set<string>();

    for (const day of week.days) {
      if (day.event && !isRestWorkout(day.event)) {
        const isRace = isRaceWorkout(day.event);
        if (isRace) {
          // Track race dates so we never overwrite them.
          raceDates.add(day.date.toISOString());
        }
        workouts.push({
          workout: day.event,
          isLong: isLongRun(day.event),
          isRace,
          date: day.date,
        });
      }
    }

    // Find the long run
    const longRunWorkout = workouts.find((w) => w.isLong && !w.isRace);
    const otherWorkouts = workouts.filter((w) => !w.isLong && !w.isRace);
    const raceWorkouts = workouts.filter((w) => w.isRace);

    // Initialize all days with rest, except race days (preserve those exactly).
    for (const day of week.days) {
      if (day.event && isRaceWorkout(day.event)) {
        newDateGrid.setEvent(day.date, day.event);
      } else {
        newDateGrid.setEvent(day.date, createRestWorkout());
      }
    }

    // Re-apply race workouts explicitly (belt-and-suspenders).
    for (const rw of raceWorkouts) {
      newDateGrid.setEvent(rw.date, rw.workout);
    }

    // Place the long run on the preferred day (never on a race day)
    if (longRunWorkout) {
      for (const day of week.days) {
        if (raceDates.has(day.date.toISOString())) continue;
        const dayOfWeek = DAY_MAP[getDay(day.date)];
        if (dayOfWeek === longRunDay) {
          newDateGrid.setEvent(day.date, longRunWorkout.workout);
          break;
        }
      }
    }

    // Place other workouts on training days (in order)
    let workoutIndex = 0;
    for (const day of week.days) {
      if (workoutIndex >= otherWorkouts.length) break;

      if (raceDates.has(day.date.toISOString())) continue;

      const dayOfWeekName = DAY_MAP[getDay(day.date)];

      // Skip if this is the long run day (already placed)
      if (dayOfWeekName === longRunDay) continue;

      // Check if this day is a training day
      if (trainingDays.includes(dayOfWeekName)) {
        const currentEvent = newDateGrid.getEvent(day.date);
        // Only place if it's currently a rest day
        if (!currentEvent || isRestWorkout(currentEvent)) {
          newDateGrid.setEvent(day.date, otherWorkouts[workoutIndex].workout);
          workoutIndex++;
        }
      }
    }

    // If we have leftover workouts, place them on any remaining training days
    for (const day of week.days) {
      if (workoutIndex >= otherWorkouts.length) break;

      if (raceDates.has(day.date.toISOString())) continue;

      const dayOfWeekName = DAY_MAP[getDay(day.date)];
      const currentEvent = newDateGrid.getEvent(day.date);

      if (trainingDays.includes(dayOfWeekName) && isRestWorkout(currentEvent)) {
        newDateGrid.setEvent(day.date, otherWorkouts[workoutIndex].workout);
        workoutIndex++;
      }
    }
  }

  // Return a new RacePlan with the modified date grid
  return {
    ...racePlan,
    dateGrid: newDateGrid,
  };
}
